/**
 * The closed sets a dialogue file's conditions and actions are checked against (ADR-136 decision 6, ADR-146 decisions 4
 * and 5): which tags are built, which are reserved to a later row, and the checks of a `when` list and a `do` list. Pure.
 */

import { NPC_STANDING_MAX, NPC_STANDING_MIN } from "../npcs/standing.ts";
import { ITEM_ID_PATTERN, ITEM_TAGS, MAX_ITEM_STACK, type ItemTag } from "../rules/types.ts";
import { isPlain, standingChangeProblem, wholeIn } from "./dialogue-check.ts";

/**
 * A standing condition (ADR-146 decision 5.1): holds while the character's standing with the conversation's NPC is at
 * least `atLeast` and at most `atMost`. One of the two is required.
 */
export interface StandingCondition {
  readonly with: "npc";
  readonly atLeast?: number;
  readonly atMost?: number;
}

/**
 * A condition that must hold for a start entry or a choice: a fact the character has or lacks, a standing with the
 * conversation's NPC, or items held (ADR-142 decision 4.1): `count` of one item, or any item with a tag.
 */
export type DialogueCondition =
  | { readonly fact: string }
  | { readonly notFact: string }
  | { readonly standing: StandingCondition }
  | { readonly item: string; readonly count: number }
  | { readonly itemTag: ItemTag }
  | { readonly companion: CompanionState };

/**
 * Where a character stands with its companion, as a condition reads it (ADR-142's amendment of 2026-10-30, decision 3):
 * `hungry` while it has not fed this in-game day, `offered` while the offer holds and has not been taken, `joined` once it
 * has, and `home` while it has joined and he is not out beside it (ADR-142 decision 6.2, P2.26-T6).
 */
export const COMPANION_STATES = Object.freeze(["hungry", "offered", "joined", "home"] as const);

/**
 * A companion state a condition names.
 */
export type CompanionState = (typeof COMPANION_STATES)[number];

/**
 * What a feed does (ADR-142's amendment of 2026-10-30, decision 3): `fed` marks a choice as a feed and starts the daily
 * wait, `accepted` (a check's `onPass` only) counts one accepted worm, `befriended` (a choice with no check) is the rare
 * worm that makes the offer hold at once.
 */
export const COMPANION_FEEDS = Object.freeze(["fed", "accepted", "befriended"] as const);

/**
 * A feed's kind.
 */
export type CompanionFeed = (typeof COMPANION_FEEDS)[number];

/**
 * The accepted worms the offer needs (ADR-142 decision 5.1), and the least a befriended feed raises the count to.
 */
export const COMPANION_OFFER_FEEDS = 3;

/**
 * The standing with the companion's NPC the offer needs, Fond (ADR-142 decisions 2.3 and 5.1), and the least a
 * befriended feed raises it to.
 */
export const COMPANION_OFFER_STANDING = 25;

/**
 * The most accepted worms the count holds, what `characters.companion_fed` allows (migration 056).
 */
export const MAX_COMPANION_FEEDS = 32767;

/**
 * An action a choice runs on the server: a change to the character's standing with the conversation's NPC (ADR-146
 * decision 4.1), a signed whole number from -100 to 100, not 0; `count` of an item taken from the bag; a fact set; or a
 * companion feed (ADR-142's amendment of 2026-10-30); or the offer accepted, which asks the player for the companion's
 * name (ADR-142 decisions 5.2 and 5.4, P2.26-T7); or the companion called out from his perch (`companionCall`, decision 6.2,
 * P2.26-T6).
 * The set grows as the reserved actions' rows open them.
 */
export type DialogueAction = { readonly standing: number } | { readonly takeItem: string; readonly count: number } | { readonly setFact: string } | { readonly companionFeed: CompanionFeed } | { readonly companionJoin: true } | { readonly companionCall: true };

/**
 * A fact's id (ADR-120): lower case, digits, dots, underscores and hyphens, at most 64 characters.
 */
export const FACT_ID_PATTERN = /^[a-z0-9][a-z0-9_.-]{0,63}$/;

/**
 * The condition tags a file may use today.
 */
export const CONDITION_TAGS = Object.freeze(["fact", "notFact"] as const);

/**
 * The condition tags that hold items (ADR-142 decision 4.1): `item` with its `count`, and `itemTag`.
 */
export const ITEM_CONDITION_TAGS = Object.freeze(["item", "itemTag"] as const);

/**
 * The condition tags the design names for later rows, refused until they are built.
 */
export const LATER_CONDITION_TAGS = Object.freeze(["level", "background"] as const);

/**
 * The reputation conditions ADR-136's amendment of 2026-10-28 reserves, each to the row that builds it.
 */
export const RESERVED_CONDITION_TAGS: { readonly [tag: string]: string } = Object.freeze({ member: "P2.22-T17", enemyOf: "P2.22-T17", notoriety: "P2.22-T18" });

/**
 * The action tags the design reserves, each refused until its row builds it: an item given once, a shop opened and a
 * quest step.
 */
export const RESERVED_ACTION_TAGS = Object.freeze(["giveItem", "openShop", "questStep"] as const);

const STANDING_CONDITION_FIELDS = ["with", "atLeast", "atMost"] as const;

/**
 * Why a `standing` condition value is refused: a closed object naming the NPC with a whole `atLeast` or `atMost` (or both,
 * in order) within -100 to 100. An organisation in `with` is reserved.
 *
 * @param value The condition's `standing` value.
 * @returns The sentence for a refused value, null for a sound one.
 */
function standingConditionProblem(value: unknown): string | null {
  if (!isPlain(value)) {
    return "standing must be an object with with and atLeast or atMost";
  }
  const extra = Object.keys(value).find((key) => !(STANDING_CONDITION_FIELDS as readonly string[]).includes(key));
  if (extra !== undefined) {
    return `${extra} is not a field of a standing condition`;
  }
  if (value["with"] !== "npc") {
    return "standing with must be npc; an organisation is refused until P2.22-T17 builds it";
  }
  const bounds = [value["atLeast"], value["atMost"]];
  if (bounds[0] === undefined && bounds[1] === undefined) {
    return "standing needs atLeast or atMost";
  }
  for (const bound of bounds) {
    if (bound !== undefined && !wholeIn(bound, NPC_STANDING_MIN, NPC_STANDING_MAX)) {
      return `standing atLeast and atMost must be whole numbers from ${NPC_STANDING_MIN} to ${NPC_STANDING_MAX}`;
    }
  }
  if (typeof bounds[0] === "number" && typeof bounds[1] === "number" && bounds[0] > bounds[1]) {
    return "standing atLeast must not be above atMost";
  }
  return null;
}

/**
 * Why an `item` or `itemTag` condition is refused: `item` names an item id and a whole `count` from 1 to a stack and
 * nothing else; `itemTag` names one of the closed item tags and nothing else.
 *
 * @param condition The condition object, whose first key is `item` or `itemTag`.
 * @returns The sentence for a refused condition, null for a sound one.
 */
function itemConditionProblem(condition: { readonly [key: string]: unknown }): string | null {
  const keys = Object.keys(condition);
  if ("itemTag" in condition) {
    if (keys.length !== 1) {
      return "an itemTag condition holds itemTag and nothing else";
    }
    return (ITEM_TAGS as readonly unknown[]).includes(condition["itemTag"]) ? null : `itemTag must be one of ${ITEM_TAGS.join(", ")}`;
  }
  const extra = keys.find((key) => key !== "item" && key !== "count");
  if (extra !== undefined) {
    return `${extra} is not a field of an item condition`;
  }
  return itemFieldsProblem(condition, "an item condition");
}

/**
 * Why an item id and a count in an object are refused: the id must match the item id pattern and the count must be a
 * whole number from 1 to a stack.
 *
 * @param value The object holding the id under `item` or `takeItem` and `count`.
 * @param what What the object is, for the sentence.
 * @returns The sentence for a refused value, null for a sound one.
 */
function itemFieldsProblem(value: { readonly [key: string]: unknown }, what: string): string | null {
  const id = "item" in value ? value["item"] : value["takeItem"];
  if (typeof id !== "string" || !ITEM_ID_PATTERN.test(id)) {
    return `${what} must name an item: lower-case letters, digits and _`;
  }
  if (!wholeIn(value["count"], 1, MAX_ITEM_STACK)) {
    return `${what} needs a count, a whole number from 1 to ${MAX_ITEM_STACK}`;
  }
  return null;
}

/**
 * Checks a list of conditions, each one tag of the closed set.
 *
 * @param value The `when` value.
 * @param report Records a problem at a path inside the list's owner.
 * @param path The path of the list.
 */
export function checkConditions(value: unknown, report: (field: string, message: string) => void, path: string): void {
  if (!Array.isArray(value)) {
    report(path, "when must be a list of conditions");
    return;
  }
  value.forEach((condition, at) => {
    const here = `${path}[${at}]`;
    const keys = isPlain(condition) ? Object.keys(condition) : [];
    const tag = keys.includes("item") ? "item" : keys[0];
    if (tag === "item" || tag === "itemTag") {
      const problem = itemConditionProblem(condition as { readonly [key: string]: unknown });
      if (problem !== null) {
        report(here, problem);
      }
    } else if (keys.length !== 1 || tag === undefined) {
      report(here, "a condition must be an object with exactly one tag");
    } else if (Object.hasOwn(RESERVED_CONDITION_TAGS, tag)) {
      report(here, `the ${tag} condition is reserved and refused until ${RESERVED_CONDITION_TAGS[tag] as string} builds it`);
    } else if ((LATER_CONDITION_TAGS as readonly string[]).includes(tag)) {
      report(here, `the ${tag} condition is not built yet`);
    } else if (tag === "companion") {
      if (!(COMPANION_STATES as readonly unknown[]).includes((condition as { readonly [key: string]: unknown })["companion"])) {
        report(here, `companion must be ${COMPANION_STATES.join(", ")}`);
      }
    } else if (tag === "standing") {
      const problem = standingConditionProblem((condition as { readonly [key: string]: unknown })["standing"]);
      if (problem !== null) {
        report(here, problem);
      }
    } else if (!(CONDITION_TAGS as readonly string[]).includes(tag)) {
      report(here, `${tag} is not a condition; use ${[...CONDITION_TAGS, ...ITEM_CONDITION_TAGS, "standing", "companion"].join(" or ")}`);
    } else if (typeof (condition as { readonly [key: string]: unknown })[tag] !== "string" || !FACT_ID_PATTERN.test((condition as { readonly [key: string]: unknown })[tag] as string)) {
      report(here, `${tag} must name a fact: lower-case letters, digits, dots, _ and -`);
    }
  });
}

/**
 * Checks a list of actions: the set is closed, so a reserved action is refused as not built and any other tag as unknown;
 * `standing` takes a whole number from -100 to 100, not 0, `takeItem` an item id and a count, `setFact` a fact id and
 * `companionFeed` one of `COMPANION_FEEDS` and `companionJoin` only `true`. Which feed or join a list may hold is
 * `companionFeedProblems`' rule.
 *
 * @param value The `do` value.
 * @param report Records a problem at a path inside the list's owner.
 * @param path The path of the list.
 */
export function checkActions(value: unknown, report: (field: string, message: string) => void, path: string): void {
  if (!Array.isArray(value)) {
    report(path, "do must be a list of actions");
    return;
  }
  value.forEach((action, at) => {
    const here = `${path}[${at}]`;
    const tag = isPlain(action) ? Object.keys(action)[0] : undefined;
    if (tag !== undefined && (RESERVED_ACTION_TAGS as readonly string[]).includes(tag)) {
      report(here, `the ${tag} action is reserved and refused until its row is built`);
    } else if (tag === "standing" && Object.keys(action as { readonly [key: string]: unknown }).length === 1) {
      const problem = standingChangeProblem((action as { readonly [key: string]: unknown })["standing"]);
      if (problem !== null) {
        report(here, problem);
      }
    } else if (tag === "standing") {
      report(here, "a standing action holds standing and nothing else");
    } else if (tag === "takeItem") {
      const fields = action as { readonly [key: string]: unknown };
      const extra = Object.keys(fields).find((key) => key !== "takeItem" && key !== "count");
      const problem = extra === undefined ? itemFieldsProblem(fields, "a takeItem action") : `${extra} is not a field of a takeItem action`;
      if (problem !== null) {
        report(here, problem);
      }
    } else if (tag === "setFact") {
      const fields = action as { readonly [key: string]: unknown };
      if (Object.keys(fields).length !== 1) {
        report(here, "a setFact action holds setFact and nothing else");
      } else if (typeof fields["setFact"] !== "string" || !FACT_ID_PATTERN.test(fields["setFact"])) {
        report(here, "setFact must name a fact: lower-case letters, digits, dots, _ and -");
      }
    } else if (tag === "companionFeed") {
      const fields = action as { readonly [key: string]: unknown };
      if (Object.keys(fields).length !== 1) {
        report(here, "a companionFeed action holds companionFeed and nothing else");
      } else if (!(COMPANION_FEEDS as readonly unknown[]).includes(fields["companionFeed"])) {
        report(here, `companionFeed must be ${COMPANION_FEEDS.join(", ")}`);
      }
    } else if (tag === "companionJoin") {
      const fields = action as { readonly [key: string]: unknown };
      if (Object.keys(fields).length !== 1 || fields["companionJoin"] !== true) {
        report(here, "a companionJoin action is { companionJoin: true } and nothing else");
      }
    } else if (tag === "companionCall") {
      const fields = action as { readonly [key: string]: unknown };
      if (Object.keys(fields).length !== 1 || fields["companionCall"] !== true) {
        report(here, "a companionCall action is { companionCall: true } and nothing else");
      }
    } else {
      report(here, tag === undefined ? "an action must be an object with a tag" : `${tag} is not an action`);
    }
  });
}

